import { UserShortModel } from "@shared/models/user";

import { CaseMessageModel } from "./CaseMessage";

export enum CASE_STATUS {
  RUNNING = "Running",
  SUCCESS = "Success",
  FAILURE = "Failure",
  PENDING = "Pending",
  INVALID = "Invalid",
}

export const PROCESSING_CASE_STATUSES = [CASE_STATUS.PENDING, CASE_STATUS.RUNNING];

export type CaseStatusModel = Pick<CaseModel, "id" | "status" | "case_person_details">;

export enum REPORT_STATUS {
  NEW = "new",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
}

export enum PAID_STATUS {
  NOT_PAID = "not_paid",
  PENDING = "pending",
  ERROR = "error",
  PAID = "paid",
}

export enum CASE_FIELD_SOURCE_MODE {
  PSR = "psr",
  MEDICAL = "medical",
  BOTH = "both",
  MANUAL = "manual",
  PENDING = "pending",
}

export enum CASE_FILE_KIND {
  PSR = "psr",
  MEDICAL = "medical",
  NOTES = "notes",
}

export enum CASE_DOCUMENT_STATUS {
  PARSING = "parsing",
  PARSED = "parsed",
  FAILED = "failed",
}

export enum CASE_DOCUMENT_STEP {
  READING = "reading",
  EXTRACTING = "extracting",
  CHECKING = "checking",
}

export enum CASE_FILE_ERROR_CODE {
  PASSWORD_PROTECTED = "password_protected",
  DAMAGED = "damaged",
  NO_TEXT = "no_text",
  SERVICE_UNAVAILABLE = "service_unavailable",
  INTERRUPTED = "interrupted",
  TOO_LONG = "too_long",
  EXTRACTION_FAILED = "extraction_failed",
}

export enum CASE_FILE_STATUS {
  PENDING = "pending",
  READING = "reading",
  EXTRACTING = "extracting",
  CHECKING = "checking",
  PROCESSED = "processed",
  FAILED = "failed",
}

export interface CaseDocumentFileModel {
  id: number;
  name: string;
  mime_type: string;
  pages: number | null;
  size: number | null;
  uploaded_at: string;
  status: CASE_FILE_STATUS;
  error_message: string | null;
  error_code: CASE_FILE_ERROR_CODE | null;
}

export interface CaseDocumentModel {
  kind: CASE_FILE_KIND;
  status: CASE_DOCUMENT_STATUS;
  step: CASE_DOCUMENT_STEP | null;
  files_read: number;
  failed_files: number;
  pending_choices: number;
  /** Low-confidence medical fields */
  flagged_fields: number;
  /** 0–100; the API doesn't send it yet */
  progress?: number;
  failed_at: string | null;
  error_message: string | null;
  error_code: CASE_FILE_ERROR_CODE | null;
  files: CaseDocumentFileModel[];
}

export type CaseMedicalField = keyof Pick<
  CaseModel,
  | "mental_health_notes"
  | "medical_history_notes"
  | "drug_alcohol_notes"
  | "conditions"
  | "diagnoses"
  | "mobility_flags"
  | "specialist_needs"
>;

export type CaseMedicalListField = keyof Pick<
  CaseModel,
  "conditions" | "diagnoses" | "mobility_flags" | "specialist_needs"
>;

export const CASE_MEDICAL_FIELDS: CaseMedicalField[] = [
  "medical_history_notes",
  "mental_health_notes",
  "drug_alcohol_notes",
  "conditions",
  "diagnoses",
  "mobility_flags",
  "specialist_needs",
];

/** Medical fields the API gives as lists. The PSR doesn't fill them, so their items come from the summaries or a user. */
export const MEDICAL_LIST_FIELDS: CaseMedicalListField[] = [
  "conditions",
  "diagnoses",
  "mobility_flags",
  "specialist_needs",
];

export enum MEDICAL_FIELD_OUTCOME {
  SAVED = "saved",
  PENDING = "pending",
  /** The field was edited by a user, so the new text wasn't saved to it */
  KEPT = "kept",
  /** The user chose to keep the field's text over the new text */
  KEPT_CURRENT = "kept_current",
  /** The summaries say nothing about the field */
  EMPTY = "empty",
}

/** What the user keeps in a medical field that has values from both the PSR or a user and the summaries. */
export enum MEDICAL_FIELD_CHOICE {
  CURRENT = "current",
  NEW = "new",
  /** The current text, a blank line, then the summaries' text */
  BOTH = "both",
}

export enum CASE_FIELD_AUTHOR {
  PSR = "psr",
  USER = "user",
}

/** The checks a medical field's summaries text goes through; a failed check flags the field low confidence. */
export enum LOW_CONFIDENCE_CHECK {
  NOT_FOUND = "not_found",
  NOT_SUPPORTED = "not_supported",
  HARD_TO_READ = "hard_to_read",
  FILES_DISAGREE = "files_disagree",
}

export enum MEDICAL_FIELD_VERIFICATION_METHOD {
  /** Checked against the source and marked verified */
  CHECKED = "checked",
  /** Edited and saved */
  EDITED = "edited",
}

/** A source line behind a flag: the text found in the file, or the quote when it wasn't found. */
export interface CaseFieldFlagSourceModel {
  text: string;
  /** Case file id */
  file_id: number;
  file: string | null;
  page: number;
}

/** Why a medical field is flagged low confidence. */
export interface CaseFieldFlagModel {
  check: LOW_CONFIDENCE_CHECK;
  text: string;
  sources: CaseFieldFlagSourceModel[];
}

export interface CaseFieldVerificationModel {
  id: number;
  method: MEDICAL_FIELD_VERIFICATION_METHOD;
  /** The name of the user who verified the field */
  created_by: string;
  created_at: string;
}

/** What parsing medical summaries did to a medical field. */
export interface MedicalFieldResultModel {
  field: CaseMedicalField;
  outcome: MEDICAL_FIELD_OUTCOME;
  text: string | null;
  /** Who wrote the field's current text, for a pending choice or kept current text only */
  current_author: CASE_FIELD_AUTHOR | null;
  /** Why the field is flagged low confidence, null when it isn't */
  flag_reason: string | null;
}

/** A medical field waiting for the user to choose between its text and the medical summaries' text. */
export interface PendingFieldChoiceModel {
  field: CaseMedicalField;
  current_text: string | null;
  current_author: CASE_FIELD_AUTHOR;
  /** The PSR file name, or the name of the user who typed the text; null when unknown */
  current_source: string | null;
  new_text: string;
  /** The medical summaries files that were read */
  new_files: number;
  /** Why the summaries' text is flagged low confidence, null when it isn't */
  flag_reason: string | null;
}

export interface CaseFieldSourceModel {
  id: number;
  case_id: number;
  field: CaseMedicalField;
  mode: CASE_FIELD_SOURCE_MODE | null;
  psr_text: string | null;
  medical_text: string | null;
  /** The typed text a field kept from both holds before the summaries' text, instead of the PSR text */
  user_text: string | null;
  /** Why the summaries' text is flagged low confidence; null when it isn't, or a user verified it */
  flags: CaseFieldFlagModel[] | null;
  verification_id: number | null;
  /** The verification that covers the summaries' text, null when none does */
  verification?: CaseFieldVerificationModel | null;
  /** Whether a user changed the field's text after its sources were saved */
  is_edited?: boolean;
  /** Whether the field shows its low-confidence flag: its text comes from the summaries and nobody edited it */
  is_flagged?: boolean;
}

export interface CaseModel {
  id: number;
  pdf_url: string;
  pdf_s3_url: string;
  status: CASE_STATUS;
  level_of_severity: number | null;
  usm_number: string | null;
  fbi_number: string | null;
  judge: string | null;
  voluntary_surrender_status: number | null;
  criminal_history_points: number | null;
  criminal_history_total_points: number | null;
  history_of_violence: number | null;
  history_of_escape: number | null;
  type_of_detainer: number | null;
  age: number | null;
  substance_abuse: number | null;
  public_safety_factors: string | null;
  remarks: string | null;
  med_history: string | null;
  drug_history: string | null;
  messages: CaseMessageModel[];
  report_status: REPORT_STATUS;

  is_deleted: boolean;

  created_at: Date;
  updated_at: Date;
  created_by: string;
  updated_by: string;

  user?: UserShortModel;

  paid_status: PAID_STATUS;

  docket_number: string | null;
  district: string | null;

  ssn_number: string | null;
  total_offense_level: number | null;
  foreign_country: string | null;
  ars_assignment: string | null;
  rec_facility: string | null;
  rec_program: string | null;
  usm_office: string | null;
  criminal_history_info: Record<string, string> | null;
  criminal_history_category: number | null;
  security_classification: string | null;

  type_of_prison: string | null;
  general_recidivism: string | null;
  first_step_act_eligible: string | null;
  second_step_act_eligible: string | null;
  risk_of_violence_recidivism: string | null;
  initial_pattern_score: string | null;
  recidivism: string | null;
  mental_health_notes: string | null;
  medical_history_notes: string | null;
  drug_alcohol_notes: string | null;
  conditions: string[];
  diagnoses: string[];
  mobility_flags: string[];
  specialist_needs: string[];
  case_notes: string | null;
  field_sources?: CaseFieldSourceModel[];
  case_documents?: CaseDocumentModel[];
  pending_choices?: PendingFieldChoiceModel[];
  program_recommendations: string | null;
  drug_program: string | null;
  vs_date: string | null;
  vs_location: string | null;
  month_to_release: string | null;
  source_doc_date: string | null;
  source_of_doc: string | null;
  highest_grade: string | null;
  omdt_referral: string | null;
  history_of_escape_status: string | null;

  case_person_details: {
    first_name: string | null;
    last_name: string | null;
    date_of_birth: string | null;
    sex: string | null;
    race: string | null;
    citizenship: string | null;
    middle_name: string | null;
    suffix: string | null;
    ssn: string;
    ethnic_origin: string | null;
    state_of_birth: string | null;
    country_of_birth: string | null;
    street: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    height: string | null;
    weight: string | null;
    hair_color: string | null;
    eyes_color: string | null;
    education_level: number | null;
  } | null;
}
